// modules/reports/InKindBaseReport.js
import * as React from "react"
import { useState, useEffect } from "react";
import {
  Title,
  useDataProvider,
  useNotify,
  usePermissions,
} from "react-admin";
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  MenuItem,
  Select,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { TextField as MuiTextField } from "@mui/material";
import {
  ExportButton,
  GenericTable,
  SummaryCards,
  OverviewCharts,
  createExportHandler,
  exportInKindData,
  ReportHeader,
  InkindTabs
} from "../components/inkind";

const token = JSON.parse(localStorage.getItem("token"));

// Main InKindBaseReport Component
const InKindBaseReport = ({
  title = "In-Kind Contributions Dashboard",
  description = "Match donations with needs and track in-kind contributions",
  type = "offers", // "offers" or "services"
  categoryEndpoint = "sub_category", // or "servicetypes"
  categoryLabel = "Category",
  resourceName = "admin/in-kind-offers",
  statisticsEndpoint = "admin/in-kind-offers/statistics"
}) => {
  const { permissions } = usePermissions();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const theme = useTheme();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(0); // MUI is 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const token = JSON.parse(localStorage.getItem("token"));

  const isService = type === "services";

  // Function to check if user is manager
  const isManager = permissions === 'manager';

  // Function to check if user can see action buttons
  const canSeeActionButtons = !isManager && (permissions === 'admin' || permissions === 'operater');
  const [statistics, setStatistics] = useState(null);

  useEffect(() => {
    const fetchMetaData = async () => {
      try {
        // Fetch states
        const stateResponse = await fetch(
          `${process.env.REACT_APP_URL}/admin/state`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );
        const statesData = await stateResponse.json();
        setStates(statesData);

        // Fetch categories based on type
        const categoriesResponse = await fetch(
          `${process.env.REACT_APP_URL}/admin/${categoryEndpoint}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: "Bearer " + token,
            },
          }
        );
        const categoriesData = await categoriesResponse.json();
        setCategories(categoriesData["data"] || categoriesData);
      } catch (err) {
        console.error("Error fetching metadata:", err);
      }
    };
    fetchMetaData();
  }, [type, categoryEndpoint, token]);

  // Update your fetchReportData function to properly handle statistics
  useEffect(() => {
    const fetchReportData = async () => {
      try {
        setLoading(true);

        // Build filter parameters
        const params = new URLSearchParams();
        const filters = [];

        if (selectedState) {
          filters.push(`state||$eq||${selectedState}`);
        }
        if (selectedCategory) {
          const categoryField = isService ? 'service_id' : 'subCategory_id';
          filters.push(`${categoryField}||$eq||${selectedCategory}`);
        }
        if (startDate) {
          filters.push(`created_at_gte||$cont||${startDate}`);
        }
        if (endDate) {
          filters.push(`created_at_lte||$cont||${endDate}`);
        }

        // Add tab-based filtering
        if (activeTab === 2) {
          if (isService) {
            filters.push(`service_type||$eq||donate`);

          } else {
            filters.push(`offer_type||$eq||donate`);
          }
          filters.push(`status||$eq||active`);
        }

        if (activeTab === 3) {
          if (isService) {
            filters.push(`service_type||$eq||need`);
          } else {
            filters.push(`offer_type||$eq||need`);
          }
          filters.push(`status||$eq||active`);
        }

        params.append('page', page + 1);
        params.append('limit', rowsPerPage);

        if (filters.length > 0) {
          params.append('filter', JSON.stringify(filters));
        }

        // Fetch in-kind data
        const url = `${process.env.REACT_APP_URL}/${resourceName}?${params.toString()}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setReportData(data);

        // Fetch statistics with the SAME FILTERS
        const statsParams = new URLSearchParams();
        if (filters.length > 0) {
          statsParams.append('filter', JSON.stringify(filters));
        }

        const statsUrl = `${process.env.REACT_APP_URL}/${statisticsEndpoint}?${statsParams.toString()}`;
        const statsResponse = await fetch(statsUrl, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        });

        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setStatistics(statsData);
        }

      } catch (err) {
        console.error("Error fetching in-kind report data:", err);
        setError(err.message || "Failed to fetch data");
        notify("Error loading in-kind report data", { type: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [selectedState, selectedCategory, startDate, endDate, token, notify, resourceName, statisticsEndpoint, isService, page, rowsPerPage, activeTab]);

  const handleAssignDelivery = () => {
    // Refresh the data after successful assignment
    refreshReportData();
    notify('Delivery company assigned successfully!', { type: 'success' });
  };

  const markAsDonated = async (id) => {
    const endpoint = isService
      ? `/admin/in-kind-services/${id}/donated`
      : `/admin/in-kind-offers/${id}/donated`;

    const response = await fetch(
      `${process.env.REACT_APP_URL}${endpoint}`,
      {
        method: 'PATCH',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
      }
    );

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to mark as donated');
    }

    return response.json();
  };

  const refreshReportData = async () => {
    setReportData(null);
    setLoading(true);

    try {
      const params = new URLSearchParams();
      const filters = [];

      if (selectedState) {
        filters.push(`state||$eq||${selectedState}`);
      }
      if (selectedCategory) {
        const categoryField = isService ? 'service_id' : 'subCategory_id';
        filters.push(`${categoryField}||$eq||${selectedCategory}`);
      }
      if (startDate) {
        filters.push(`created_at_gte||$cont||${startDate}`);
      }
      if (endDate) {
        filters.push(`created_at_lte||$cont||${endDate}`);
      }

      // Add tab-based filtering
      if (activeTab === 2) {
        filters.push(`offer_type||$eq||donate`);
        filters.push(`status||$eq||active`);
      }

      if (activeTab === 3) {
        filters.push(`offer_type||$eq||need`);
        filters.push(`status||$eq||active`);
      }

      // params.append('page', '1');
      // params.append('limit', '50');

      params.append('page', page + 1);
      params.append('limit', rowsPerPage);

      if (filters.length > 0) {
        params.append('filter', JSON.stringify(filters));
      }

      const url = `${process.env.REACT_APP_URL}/${resourceName}?${params.toString()}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setReportData(data);

      // Also fetch statistics
      const statsResponse = await fetch(
        `${process.env.REACT_APP_URL}/${statisticsEndpoint}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        }
      );

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setReportData(prev => ({ ...prev, statistics: statsData }));
      }

    } catch (err) {
      console.error("Error refreshing report data:", err);
      setError(err.message || "Failed to refresh data");
      notify("Error refreshing in-kind report data", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsDonated = async (offerId) => {
    try {
      await markAsDonated(offerId);
      notify('Successfully marked as donated!', { type: 'success' });
      await refreshReportData();
    } catch (error) {
      console.error('Error marking as donated:', error);
      notify('Failed to mark as donated', { type: 'error' });
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 3 }}>
        Failed to load in-kind report data. Please try again later.
        <Box mt={1} fontFamily="monospace">
          {error}
        </Box>
      </Alert>
    );
  }

  if (!reportData) {
    return (
      <Alert severity="warning" sx={{ my: 3 }}>
        No in-kind report data available.
      </Alert>
    );
  }

  // Calculate stats from data
  const items = reportData?.data || [];
  // const donations = items.filter(item => {
  //   const typeField = isService ? item.service_type : item.offer_type;
  //   return typeField === 'donate';
  // });
  // const needs = items.filter(item => {
  //   const typeField = isService ? item.service_type : item.offer_type;
  //   return typeField === 'need';
  // });
  // const activeDonations = donations.filter(d => d.status === 'active');
  // const activeNeeds = needs.filter(n => n.status === 'active');

  // Create export handlers for each tab
  const exportAllItemsHandler = createExportHandler(
    items,
    `All_In_Kind_${type === 'services' ? 'Services' : 'Offers'}`,
    isService,
    notify
  );

  const exportActiveDonationsHandler = createExportHandler(
    // activeDonations,
    items,
    'Active_Donations',
    isService,
    notify
  );

  const exportActiveNeedsHandler = createExportHandler(
    // activeNeeds,
    items,
    'Active_Needs',
    isService,
    notify
  );

  // Function to export all filtered data
  const exportAllFilteredData = async () => {
    try {
      // Build filter parameters for export
      const params = new URLSearchParams();
      const filters = [];

      if (selectedState) {
        filters.push(`state||$eq||${selectedState}`);
      }
      if (selectedCategory) {
        const categoryField = isService ? 'service_id' : 'subCategory_id';
        filters.push(`${categoryField}||$eq||${selectedCategory}`);
      }
      if (startDate) {
        filters.push(`created_at_gte||$cont||${startDate}`);
      }
      if (endDate) {
        filters.push(`created_at_lte||$cont||${endDate}`);
      }

      // Fetch all data (no pagination)
      params.append('page', '1');
      params.append('limit', '10000');

      if (filters.length > 0) {
        params.append('filter', JSON.stringify(filters));
      }

      const url = `${process.env.REACT_APP_URL}/${resourceName}?${params.toString()}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const allData = data.data || [];

      if (allData.length === 0) {
        notify('No data to export', { type: 'warning' });
        return;
      }

      // Filter data based on active tab
      let filteredData = allData;
      let exportTitle = `All_${type === 'services' ? 'Services' : 'Offers'}`;

      switch (activeTab) {
        case 2: // Active donations
          filteredData = allData.filter(item => {
            const typeField = isService ? item.service_type : item.offer_type;
            return typeField === 'donate' && item.status === 'active';
          });
          exportTitle = 'Active_Donations';
          break;
        case 3: // Active needs
          filteredData = allData.filter(item => {
            const typeField = isService ? item.service_type : item.offer_type;
            return typeField === 'need' && item.status === 'active';
          });
          exportTitle = 'Active_Needs';
          break;
      }

      exportInKindData(filteredData, exportTitle, isService);
      notify(`Exported ${filteredData.length} records successfully!`, { type: 'success' });

    } catch (error) {
      console.error('Export error:', error);
      notify('Error exporting data: ' + error.message, { type: 'error' });
    }
  };

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.background.default,
          0.9
        )} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        p: 3,
        borderRadius: "16px",
      }}
    >
      <Title title={`${title} Reports`} />

      <ReportHeader
        title={title}
        description={description}
        type={type}
        canSeeActionButtons={canSeeActionButtons}
      // theme={theme}
      />

      {/* Summary Cards */}
      <SummaryCards statistics={statistics} />

      {/* Tabs */}
      <InkindTabs
        theme={theme}
        type={type}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        setPage={setPage}
      />

      {/* Overview Tab */}
      {activeTab === 0 && (
        <>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2">State</Typography>
              <Select
                fullWidth
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                {states.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2">{categoryLabel}</Typography>
              <Select
                fullWidth
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2">From</Typography>
              <DatePicker
                value={startDate || null}
                onChange={(newDate) => {
                  const iso = newDate ? newDate.toISOString().slice(0, 10) : "";
                  setStartDate(iso);
                }}
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    fullWidth
                    size="small"
                    inputProps={{ ...params.inputProps, readOnly: true }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2">To</Typography>
              <DatePicker
                value={endDate || null}
                onChange={(newDate) => {
                  const iso = newDate ? newDate.toISOString().slice(0, 10) : "";
                  setEndDate(iso);
                }}
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    fullWidth
                    size="small"
                    inputProps={{ ...params.inputProps, readOnly: true }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <OverviewCharts reportData={reportData} categoryLabel={categoryLabel} isService={isService} theme={theme} />
        </>
      )}

      {/* All In-Kind Items Tab */}
      {activeTab === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            All In-Kind {type === 'services' ? 'Services' : 'Offers'} ({items.length})
          </Typography>

          <Box display="flex" gap={1}>
            <ExportButton
              onClick={exportAllItemsHandler}
              data={items}
              label="Export Current View"
            />
            <ExportButton
              onClick={exportAllFilteredData}
              data={items}
              label="Export All Filtered"
            />
          </Box>

          <GenericTable
            data={items}
            title={`in-kind ${type === 'services' ? 'services' : 'offers'}`}
            onMarkAsDonated={handleMarkAsDonated}
            onAssignDelivery={handleAssignDelivery}
            isService={isService}
            resourceName={resourceName}
            showAssignButton={canSeeActionButtons}
            canSeeActionButtons={canSeeActionButtons}
            page={page}
            rowsPerPage={rowsPerPage}
            total={reportData.total}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            onEdit={(id) => {
              // Navigate to edit page
              window.location.href = `#/in-kind-offers/${id}`;
            }}
            onDelete={async (id) => {
              if (window.confirm('Are you sure you want to delete this item?')) {
                try {
                  const response = await fetch(`${process.env.REACT_APP_URL}/${resourceName}/${id}`, {
                    method: 'DELETE',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json',
                    },
                  });

                  if (!response.ok) throw new Error('Delete failed');

                  notify('Item deleted successfully', { type: 'success' });
                  refreshReportData(); // Refresh the data after delete
                } catch (error) {
                  console.error('Delete error:', error);
                  notify('Failed to delete item', { type: 'error' });
                }
              }
            }}
            canEdit={canSeeActionButtons}
            canDelete={canSeeActionButtons}
          />
        </Box>
      )}

      {/* Active Donations Tab */}
      {activeTab === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Active Donations
            {/* ({activeDonations.length}) */}
            ({items.length})
          </Typography>

          <Box display="flex" gap={1}>
            <ExportButton
              onClick={exportActiveDonationsHandler}
              // data={activeDonations}
              data={items}
              label="Export Current View"
            />
            <ExportButton
              onClick={exportAllFilteredData}
              // data={activeDonations}
              data={items}
              label="Export All Filtered"
            />
          </Box>

          <GenericTable
            // data={activeDonations}
            data={items}
            title="active donations"
            onMarkAsDonated={handleMarkAsDonated}
            isService={isService}
            resourceName={resourceName}
            showAssignButton={canSeeActionButtons}
            canSeeActionButtons={canSeeActionButtons}
            page={page}
            rowsPerPage={rowsPerPage}
            total={reportData.total}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            onEdit={(id) => {
              // Navigate to edit page
              window.location.href = `#/in-kind-offers/${id}`;
            }}
            onDelete={async (id) => {
              if (window.confirm('Are you sure you want to delete this item?')) {
                try {
                  const response = await fetch(`${process.env.REACT_APP_URL}/${resourceName}/${id}`, {
                    method: 'DELETE',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json',
                    },
                  });

                  if (!response.ok) throw new Error('Delete failed');

                  notify('Item deleted successfully', { type: 'success' });
                  refreshReportData(); // Refresh the data after delete
                } catch (error) {
                  console.error('Delete error:', error);
                  notify('Failed to delete item', { type: 'error' });
                }
              }
            }}
            canEdit={canSeeActionButtons}
            canDelete={canSeeActionButtons}
          />
        </Box>
      )}

      {/* Active Needs Tab */}
      {activeTab === 3 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Active Needs
            {/* ({activeNeeds.length}) */}
            ({items.length})
          </Typography>

          <Box display="flex" gap={1}>
            <ExportButton
              onClick={exportActiveNeedsHandler}
              // data={activeNeeds}
              data={items}
              label="Export Current View"
            />
            <ExportButton
              onClick={exportAllFilteredData}
              // data={activeNeeds}
              data={items}
              label="Export All Filtered"
            />
          </Box>

          <GenericTable
            // data={activeNeeds}
            data={items}
            title="active needs"
            onMarkAsDonated={handleMarkAsDonated}
            isService={isService}
            resourceName={resourceName}
            showAssignButton={canSeeActionButtons}
            canSeeActionButtons={canSeeActionButtons}
            page={page}
            rowsPerPage={rowsPerPage}
            total={reportData.total}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            onEdit={(id) => {
              // Navigate to edit page
              window.location.href = `#/in-kind-offers/${id}`;
            }}
            onDelete={async (id) => {
              if (window.confirm('Are you sure you want to delete this item?')) {
                try {
                  const response = await fetch(`${process.env.REACT_APP_URL}/${resourceName}/${id}`, {
                    method: 'DELETE',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json',
                    },
                  });

                  if (!response.ok) throw new Error('Delete failed');

                  notify('Item deleted successfully', { type: 'success' });
                  refreshReportData(); // Refresh the data after delete
                } catch (error) {
                  console.error('Delete error:', error);
                  notify('Failed to delete item', { type: 'error' });
                }
              }
            }}
            canEdit={canSeeActionButtons}
            canDelete={canSeeActionButtons}
          />
        </Box>
      )}
    </Box>
  );
};

export default InKindBaseReport;